import { useLocale } from "../../hooks/useLocale";
import { msg } from "../../locales/translate";
import { Box } from "@ui/box/Box";
import Button from "@ui/button/Button";
import Spacer from "@ui/spacer/Spacer";
import { Typo, TypoCaption } from "@ui/text/Typo";
import { useEffect, useState } from "react";
import { GD } from "src/GD";
import { useMobile } from "src/hooks/useMobile";
import { FormParser } from "../FormParser";
import { StartScreenView } from "../StartScreenView";
import { S_AUTH_CHANGE_VIEW } from "./AuthSignals";

const form = {
	code: "",
	get $__code_label() { return msg("oneTimeCode"); },
};

type TTwoFactorForm = typeof form;

export const AuthTwoFactor = () => {
	useLocale();
	const [busy, setBusy] = useState(false);
	const [pending] = GD.REQ_AUTH_2FA_PENDING.useRequest();
	const { isMobile, isTablet } = useMobile();
	const isDeviceVerification = pending?.type === "device";
	const title = isDeviceVerification ? msg("verifyThisDevice") : msg("verifyYourSignIn");
	const description = isDeviceVerification
		? msg("enterTheCodeFromTheEmailNotificationToConfirmThisBrowserAndC_018ebf00")
		: msg("enterTheOneTimeCodeFromYourEmailOrAuthenticatorApp");
	const submitTitle = isDeviceVerification ? msg("verifyDevice") : msg("verifyCode");

	useEffect(() => {
		if (pending === null) {
			S_AUTH_CHANGE_VIEW.invoke("login");
		}
	}, [pending]);

	return (
		<StartScreenView busy={busy}>
			<Box
				sx={{
					width: isMobile ? "100%" : undefined,
					overflow: isMobile ? "auto" : undefined,
					display: "flex",
					height: "100%",
					minHeight: isMobile ? undefined : "700px",
					borderStartStartRadius: isMobile ? "0" : "12px",
					borderEndStartRadius: isMobile ? "0" : "12px",
					borderEndEndRadius: isTablet ? "12px" : "0",
					borderStartEndRadius: isTablet ? "12px" : "0",
					backgroundColor: "rgba(255,255,255,1)",
				}}>
				<Box
					sx={{
						padding: isMobile ? "34px 26px" : "60px",
						minWidth: isMobile ? "0" : "500px",
						maxWidth: "500px",
						width: isMobile ? "100%" : "auto",
						display: "flex",
						margin: isMobile ? "auto" : undefined,
						flexDirection: "column",
					}}>
					<Typo capitalize sx={{ fontSize: "26px", fontWeight: 400 }}>
						{title}
					</Typo>
					<TypoCaption sx={{ color: "rgba(15,23,42,0.64)", lineHeight: "20px", marginTop: "8px" }}>{description}</TypoCaption>
					<Spacer size="40px" />

					{pending !== null && (
						<FormParser<TTwoFactorForm>
							key="authTwoFactorForm"
							ignoreObserver={true}
							secondaryButton={null}
							fullSizeButtons={true}
							primaryButton={{ title: submitTitle, variant: "primary", sx: { flexGrow: "1" } }}
							onSubmit={async (data: TTwoFactorForm) => {
								setBusy(true);
								const response = await GD.REQ_AUTH_VERIFY_2FA.request({
									code: data.code,
								});
								setBusy(false);

								if (response) {
									return { data, error: response };
								}

								return { data };
							}}
							onRequest={async (): Promise<TTwoFactorForm> => ({ ...form })}
						/>
					)}

					<Box
						sx={{
							display: "flex",
							gap: "5px",
							alignItems: "center",
							margin: "25px 0",
						}}>
						<Box sx={{ border: "1px solid rgba(0,0,0,0.14)", width: "100%" }} />
						<TypoCaption italic>{msg("or")}</TypoCaption>
						<Box sx={{ border: "1px solid rgba(0,0,0,0.14)", width: "100%" }} />
					</Box>
					<Button
						sx={{
							width: "auto",
						}}
						variant="secondary"
						onClick={() => S_AUTH_CHANGE_VIEW.invoke("login")}>{msg("backToSignIn")}</Button>
				</Box>
			</Box>
		</StartScreenView>
	);
};
